import { useQuery } from "@tanstack/react-query";
import { ArrowUpRightIcon, HistoryIcon, MapPinIcon } from "lucide-react";
import { AppHeader } from "@/components/app/app-header";
import { DirectoryHeading } from "@/components/app/directory-heading";
import { DiscFlightArt } from "@/components/app/disc-flight-art";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useGlassSurface } from "@/lib/ground";
import { tournamentStatsQuery, tournamentsQuery } from "@/lib/queries";
import { lastTournament, tournamentHref } from "@/lib/tournament";
import { useCalendarNow } from "@/lib/use-calendar-now";
import { cn } from "@/lib/utils";
import {
  doorOpen,
  phaseOf,
  type TournamentPhase,
  type TournamentStats,
  type TournamentSummary,
} from "../../shared/tournament";
import { useT } from "../i18n";
import { dateRangeOf } from "../i18n/format";
import { NextMatchCard } from "./front/NextMatchCard";

/**
 * The front page: every published tournament, grouped by when it is
 * (V2-ROADMAP.md Phase 2, research D18).
 *
 * LEAN ON PURPOSE. This is the main chunk — a QR landing paints from it and
 * an installed board starts here — so it is one registry read and a few cards:
 * no crest carousel, no per-tournament live reads, no library. "Now",
 * "upcoming" and "past" are DERIVED from dates and the optional end time on
 * the event's calendar (`phaseOf`), never stored, so nothing has to be flipped at
 * midnight by anyone.
 *
 * The last-opened event has a compact return link, while the programme keeps
 * every event in its calendar group. The nearest current/upcoming event gets
 * the poster treatment without an extra request or an invented cover image.
 */
export function FrontPage() {
  useGlassSurface();
  const t = useT();
  const query = useQuery(tournamentsQuery());
  // The numbers on the cards: a second, slow read, so the list itself stays
  // one registry hop and paints first.
  const stats = useQuery(tournamentStatsQuery()).data ?? {};
  const all = (query.data ?? []).filter((row) => row.status !== "draft");
  const today = useCalendarNow();
  const last = lastTournament();
  const recent = last === null ? undefined : all.find((x) => x.slug === last);

  // The public directory stays about published events, even for an organiser.
  // Draft work is reachable through the account's Organising list.
  const groups: Record<TournamentPhase, TournamentSummary[]> = { now: [], upcoming: [], past: [] };
  for (const row of all) {
    groups[phaseOf(row, today)].push(row);
  }
  // What is on and what is coming, soonest first; what is over, latest first.
  groups.now.sort((a, b) => a.startDate.localeCompare(b.startDate));
  groups.upcoming.sort((a, b) => a.startDate.localeCompare(b.startDate));
  groups.past.sort((a, b) => b.endDate.localeCompare(a.endDate));
  const spotlight = groups.now[0] ?? groups.upcoming[0];

  const sections: { id: TournamentPhase; label: string }[] = [
    { id: "now", label: t("front.now") },
    { id: "upcoming", label: t("front.upcoming") },
    { id: "past", label: t("front.past") },
  ];

  return (
    <div className="min-h-dvh pb-12">
      <div className="board-ground" aria-hidden="true" />
      <AppHeader />

      <main className="mx-auto flex w-full max-w-3xl flex-col gap-8 px-4 py-7 adm:max-w-6xl adm:gap-10 adm:py-10">
        <header className="flex flex-col gap-5 adm:flex-row adm:items-end adm:justify-between adm:gap-10">
          <div className="min-w-0 space-y-2">
            <DirectoryHeading>{t("front.eyebrow")}</DirectoryHeading>
            <p className="max-w-md text-sm leading-relaxed text-muted-foreground sm:text-base">
              {t("front.intro")}
            </p>
          </div>
          {recent !== undefined && (
            <a
              href={tournamentHref(recent.slug)}
              className="group flex min-w-0 items-center gap-3 self-start rounded-xl border px-4 py-3 transition-colors hover:bg-accent focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-ring adm:max-w-xs adm:self-auto"
            >
              <HistoryIcon aria-hidden="true" className="size-4 shrink-0 text-muted-foreground" />
              <span className="min-w-0 space-y-1">
                <span className="block text-xs text-muted-foreground">{t("front.lastOpened")}</span>
                <span className="block text-sm font-medium group-hover:underline [overflow-wrap:anywhere]">
                  {recent.name}
                </span>
              </span>
              <ArrowUpRightIcon aria-hidden="true" className="size-4 shrink-0" />
            </a>
          )}
        </header>
        {query.isError && all.length === 0 && (
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground text-pretty">{t("front.loadFailed")}</p>
            <Button type="button" onClick={() => void query.refetch()}>
              {t("common.retry")}
            </Button>
          </div>
        )}

        {/* Before the list: the reader's own game, or what the fields are
            playing (2026-09-16). One more small read, after the registry
            has painted; nothing when nothing is on. */}
        <NextMatchCard tournaments={all} featuredSlug={last ?? undefined} />

        {sections.map(({ id, label }) =>
          groups[id].length === 0 ? null : (
            <Group key={id} label={label}>
              {groups[id].map((row) => (
                <TournamentCard
                  key={row.id}
                  row={row}
                  today={today}
                  stats={stats[row.id]}
                  spotlight={row.id === spotlight?.id}
                />
              ))}
            </Group>
          ),
        )}

        {query.isSuccess && all.length === 0 && (
          <div className="rounded-xl border border-dashed bg-card/50 px-4 py-8 text-center text-sm text-muted-foreground text-pretty">
            {t("front.empty")}
          </div>
        )}
      </main>
    </div>
  );
}

function Group({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <section className="space-y-4">
      <h2 className="nxt text-xl leading-none">{label}</h2>
      <div className="grid gap-4 adm:grid-cols-2">{children}</div>
    </section>
  );
}

/**
 * One tournament, one tap. The whole card is the link (a thumb at a fence),
 * and it says what a spectator decides by: the name IN FULL (a truncated
 * "UKRAINIAN OPEN NATI…" told nobody which event it was), the days, the
 * place, and a line of numbers. Current and upcoming events lead; a past visit
 * must not displace the next event. The flight art stays in its own column,
 * never behind the name or data, and is decorative rather than a cover photo.
 */
function TournamentCard({
  row,
  today,
  stats,
  spotlight = false,
}: {
  row: TournamentSummary;
  today: Date;
  stats?: TournamentStats | undefined;
  spotlight?: boolean;
}) {
  const t = useT();
  const phase = phaseOf(row, today);
  const live = phase === "now";
  // Nothing to say about an event with no teams yet: "0 teams" is noise on
  // a card, not information.
  const numbers =
    stats === undefined || stats.teams === 0
      ? []
      : [
          t("front.stat.teams", { count: stats.teams }),
          ...(stats.players > 0 ? [t("front.stat.players", { count: stats.players })] : []),
          ...(stats.divisions > 1 ? [t("front.stat.divisions", { count: stats.divisions })] : []),
          ...(stats.games === 0
            ? []
            : phase === "upcoming" || stats.played === stats.games
              ? [t("front.stat.games", { count: stats.games })]
              : [t("front.stat.played", { played: stats.played, count: stats.games })]),
        ];
  return (
    <a
      href={tournamentHref(row.slug)}
      className={cn(
        "board-pane group flex min-w-0 flex-col overflow-hidden rounded-2xl border bg-card transition-colors hover:bg-accent focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-ring",
        spotlight && "adm:col-span-2",
      )}
    >
      <div
        className={cn(
          "relative flex flex-1 flex-col gap-5 p-5 sm:p-6",
          spotlight &&
            "sm:grid sm:grid-cols-[minmax(0,1fr)_12rem] sm:items-center sm:gap-8 adm:grid-cols-[minmax(0,1fr)_19rem] adm:p-8",
        )}
      >
        <div className="relative z-10 min-w-0 space-y-4">
          <div
            className={cn(
              "flex flex-wrap items-center gap-x-4 gap-y-2",
              spotlight && "min-h-14 pr-24 sm:min-h-0 sm:pr-0",
            )}
          >
            <span className={cn("text-sm font-medium tabular-nums", spotlight && "sm:text-base")}>
              {dateRangeOf(row.startDate, row.endDate)}
            </span>
            {live && <Badge className="border-go/30 bg-go/10 text-go-ink">{t("front.now")}</Badge>}
            {row.status === "archived" && <Badge variant="outline">{t("front.archived")}</Badge>}
            {doorOpen(row, row.wallNow) && (
              <Badge className="border-brand/40 bg-brand/10 text-brand-ink">
                {t("front.registration")}
              </Badge>
            )}
          </div>
          <h3
            className={cn(
              "nxt text-2xl leading-tight text-balance uppercase [overflow-wrap:anywhere] sm:text-3xl",
              spotlight && "text-3xl sm:text-4xl adm:text-5xl",
            )}
          >
            {row.name}
          </h3>
          {row.city && (
            <span className="flex items-center gap-2 text-sm text-muted-foreground">
              <MapPinIcon aria-hidden="true" className="size-3.5 shrink-0" />
              {row.city}
            </span>
          )}
        </div>
        {spotlight && (
          <div
            aria-hidden="true"
            className="pointer-events-none absolute right-4 top-4 w-24 sm:static sm:w-auto"
          >
            <DiscFlightArt />
          </div>
        )}
      </div>
      <div
        className={cn(
          "flex flex-wrap items-center justify-between gap-x-6 gap-y-4 border-t px-5 py-4 sm:px-6",
          spotlight && "adm:px-8",
        )}
      >
        {numbers.length > 0 ? (
          <span className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground sm:text-sm">
            {numbers.map((number) => (
              <span key={number}>{number}</span>
            ))}
          </span>
        ) : (
          <span className="text-sm text-muted-foreground">{t("front.explore")}</span>
        )}
        <span
          className={cn(
            "flex items-center gap-2 text-sm font-semibold",
            spotlight && "text-brand-ink dark:text-brand",
          )}
        >
          {spotlight && <span>{t("front.openTournament")}</span>}
          <ArrowUpRightIcon aria-hidden="true" className="size-5 shrink-0" />
        </span>
      </div>
    </a>
  );
}
